import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { getIpLocale, getWeatherForecast } from 'api/account';
import dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import './weather-widget.scss';
import { Loading } from 'components/Loading';
import { APTooltipProvider } from 'components/Tooltip';
import clsx from 'clsx';

dayjs.extend(customParseFormat);

const WeatherWidgetInner = () => {
  const isMobileDashboard = useMediaQuery({ maxWidth: 767 });
  const [weather, setWeather] = useState(null);
  const [ipLocation, setIpLocation] = useState(null);
  const ipLocationRef = useRef(ipLocation);
  const [weatherForecast, setWeatherForecast] = useState(null);
  const [isForecastVisible, setForecastVisible] = useState(false);

  useEffect(() => {
    ipLocationRef.current = ipLocation;
  }, [ipLocation]);

  useEffect(() => {
    const fetchWeather = async () => {
      let ipResponse = ipLocationRef.current;
      if (!ipResponse) {
        ipResponse = await getIpLocale();
        if (!ipResponse || !ipResponse?.data || !ipResponse?.data?.ip) return;
        setIpLocation(ipResponse);
      }

      const response = await getWeatherForecast(ipResponse.data.ip);
      const weatherResponse = response;

      setWeather(prev => {
        return weatherResponse?.data?.current || prev;
      });
      setWeatherForecast(prev => {
        return weatherResponse?.data?.forecast?.forecastday || prev;
      });
    };
    fetchWeather();
    // Check weather every minute
    const interval = setInterval(fetchWeather, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!weather || !weatherForecast || !ipLocation) {
    return (
      <div className={clsx('weather-wrapper', isMobileDashboard && 'weather-wrapper--mobile-compact')}>
        <div className={clsx('weather', isMobileDashboard && 'weather--mobile-compact')}>
          <Loading />
        </div>
      </div>
    );
  }

  if (isMobileDashboard) {
    return (
      <div className="weather-wrapper weather-wrapper--mobile-compact">
        <div className="weather weather--mobile-compact">
          <div className="weather__mobile-compact-inner">
            {weather?.condition?.icon ? (
              <>
                <img src={weather.condition.icon}
                  alt=""
                />
                <div className="weather__temp-mobile">{Math.round(weather.temp_c)}°</div>
              </>
            ) : (
              <Loading />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('weather-wrapper', isForecastVisible && 'expanded')}>
      <div className="weather">
        <div
          className="weather__image"
          onClick={() => setForecastVisible(prev => !prev)}
        >
          {
            weather?.condition?.icon ?
              (
                <>
                  <img src={weather.condition.icon} />
                  <div className="weather__condition">{weather.condition.text}</div>
                </>
              ) :
                <Loading />
          }
        </div>
        <div className="vr" />
        <div className="weather__content">
          <div className="weather__data">
            <div className="weather__day">
              {weatherForecast[0] ? dayjs(weatherForecast[0].date).format('ddd, MMM DD') : ''}
            </div>
            <div className="weather__city">{ipLocation.data.city}, {ipLocation.data.country_code_iso3}</div>
            <div className="weather__temp">
              {weather.temp_c}° C / {weather.temp_f}° F
            </div>
          </div>
        </div>
      </div>
      {isForecastVisible && (
        <div className="weather-list">
          {weatherForecast.map((forecast, index) => {
            if (index === 0) return null;
            return (
              <>
                <div
                  className="weather"
                  key={`forecast-${index}`}
                >
                  <div className="weather__image">
                    <img src={forecast.day.condition.icon} />
                    <div className="weather__condition">{forecast.day.condition.text}</div>
                  </div>
                  <div className="vr" />
                  <div className="weather__content">
                    <div className="weather__data">
                      <div className="weather__day">{dayjs(forecast.date).format('ddd, MMM DD')}</div>
                      <div className="weather__city">
                        {ipLocation.data.city}, {ipLocation.data.country_code_iso3}
                      </div>
                      <div className="weather__temp">
                        {forecast.day.avgtemp_c}° C / {forecast.day.avgtemp_f}° F
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WeatherWidget = (props) => (
  <APTooltipProvider
    tooltipDelay={300}
    floatingProps={{ placement: 'top' }}
  >
    <WeatherWidgetInner {...props} />
  </APTooltipProvider>
);

export default WeatherWidget;
